import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Divider,
  Menu,
  MenuItem,
  Button as MuiButton,
  ButtonGroup as MuiButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/system';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MultiValue, SingleValue } from 'react-select';
import { CASES_CSV } from 'src/api';
import axios from 'src/axios';
import * as Types from 'src/types';
import { Option } from 'src/types';
import { downloadFile } from 'src/utils/commonFunctions';
import { DateFormats, formatDate, getNow } from 'src/utils/time';
import { FormInput } from '../FormInput';
import { Modal } from '../Modal';
import { handleErrorToast } from '../Toast';
import { CaseDataDownloadAuditLogTable } from './CaseDataDownloadAuditLogTable';

enum CaseDataOptions {
  DOWNLOAD = 'Export data',
  VIEW = 'View audit log',
}

export const CaseDataDownloadButton = () => {
  const modalTitle = 'Download all case data?';
  const modalDescription =
    'You are about to download sensitive data outside of Vault Platform. As authorized personnel, you are entrusted with protecting your organisations’ data and all persons concerned. Remember to follow internal policies and restrict access solely to authorized personnel.';
  const modalConfirmButtonText = 'Download';
  const toastMessage = 'Oops! Something went wrong';
  const toastDescription =
    "Sorry, we can't download the data now. Please check your connection and try again later.";
  const buttonText = 'Export case data';

  const [loading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );

  const { tokens } = useTheme<LensTheme>();

  const getCaseDataDownload = async () => {
    const currentTimestamp = getNow({ returnAsDate: true });
    const dateFormatted = formatDate(
      currentTimestamp,
      DateFormats.YYYY_MM_DD_HH_MM,
    );
    const filename = `CaseDownload-${dateFormatted}.csv`;

    setLoading(true);

    try {
      const response = await axios.get(CASES_CSV, { timeout: 60000 });
      downloadFile(new Blob([response.data]), filename);

      setLoading(false);
      setConfirmModalOpen(false);
    } catch (error) {
      setLoading(false);

      handleErrorToast({
        toastMessage,
        toastDescription,
      });
    }
  };

  return (
    <div>
      <Modal
        size="medium"
        title={modalTitle}
        description={modalDescription}
        confirmButtonText={modalConfirmButtonText}
        open={confirmModalOpen}
        setOpen={setConfirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={getCaseDataDownload}
        loading={loading}
      />

      <CaseDataDownloadAuditLogTable
        isOpen={auditLogOpen}
        setIsOpen={setAuditLogOpen}
      />

      {isLegacyMode && (
        <FormInput
          variant={Types.FormInputVariant.DROPDOWN}
          data-testid={'view-case-data'}
          buttonText={buttonText}
          dropdownButtonSize="medium"
          isMulti={false}
          hideDropdownIndicator={false}
          buttonVariant={'secondary'}
          options={[
            { label: CaseDataOptions.DOWNLOAD, value: 'download' },
            { label: CaseDataOptions.VIEW, value: 'view' },
          ]}
          onChange={(option: SingleValue<Option> | MultiValue<Option>) => {
            if (option && 'value' in option) {
              if (option.value === 'download') {
                setConfirmModalOpen(true);
              } else {
                setAuditLogOpen(true);
              }
            }
          }}
          controlShouldRenderValue={false}
          $zIndex={1000}
          withBorder={true}
          downloadIcon={true}
        />
      )}
      {!isLegacyMode && (
        <>
          <MuiButtonGroup
            ref={anchorRef}
            variant="outlined"
            disableElevation
            data-testid="export-case-data-button"
            sx={{
              gap: '1.5px !important',
              '& .MuiButtonGroup-grouped:first-of-type': {
                borderRadius: '12px 0 0 12px',
              },
              '& .MuiButtonGroup-grouped:last-of-type': {
                borderRadius: '0 12px 12px 0',
              },
              '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                borderRight: `1px solid ${tokens.semantic.color.action.primary.onPrimary.value}40`,
              },
            }}
          >
            <MuiButton
              data-testid="export-case-data-main-button"
              onClick={() => {
                setConfirmModalOpen(true);
                setMenuOpen(false);
              }}
              sx={{
                textTransform: 'none',
                padding: '8px 20px',
                whiteSpace: 'nowrap',
                '& .MuiButton-startIcon': {
                  marginRight: '6px',
                },
              }}
            >
              Export case data
            </MuiButton>
            <MuiButton
              data-testid="export-case-data-more-options"
              aria-label="More options"
              onClick={() => setMenuOpen(true)}
              sx={{ minWidth: '36px', padding: '8px 12px' }}
            >
              <i className="uil uil-angle-down" />
            </MuiButton>
          </MuiButtonGroup>
          <Menu
            open={menuOpen}
            anchorEl={anchorRef.current}
            onClose={() => setMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            data-testid="export-case-data-menu"
            slotProps={{
              paper: {
                sx: {
                  minWidth: anchorRef.current?.offsetWidth ?? 'auto',
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setConfirmModalOpen(true);
                setMenuOpen(false);
              }}
              data-testid="export-case-data"
            >
              Export data
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAuditLogOpen(true);
                setMenuOpen(false);
              }}
              data-testid="view-audit-log"
            >
              View audit log
            </MenuItem>
          </Menu>
        </>
      )}
    </div>
  );
};
